import type {
  ActBody,
  ActResponse,
  ChallengeGame,
  CpuStats,
  CreateRoomResponse,
  CyclesSettings,
  DailyEntry,
  DungeonToday,
  DungeonView,
  GameId,
  HistoryEntry,
  LeaderboardEntry,
  MeResponse,
  OnlineResponse,
  ProfilePatch,
  RoomMode,
  RoomSummary,
  RunSummary,
  StartRunBody,
  TodayResponse,
  TransferCodeResponse,
} from '@xox/shared'

export class ApiError extends Error {
  readonly status: number
  /** The parsed JSON error body, when there was one (a 409 carries the fresh dungeon view). */
  readonly body: unknown
  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.status = status
    this.body = body
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    let message = res.statusText
    let body: unknown
    try {
      body = await res.json()
      const b = body as { error?: string; message?: string }
      message = b.error ?? b.message ?? message
    } catch {
      // non-JSON error body
    }
    throw new ApiError(res.status, message, body)
  }
  return (await res.json()) as T
}

export type Me = MeResponse & { activeRooms: Record<GameId, string | null> }

const DUEL_PATHS: Record<GameId, { rooms: string; stats: string }> = {
  xox: { rooms: '/api/rooms', stats: '/api/xox/cpu/stats' },
  c4: { rooms: '/api/c4/rooms', stats: '/api/c4/cpu/stats' },
}

/** Room + bot endpoints of one two-seat game; settings are that game's own shape. */
export function duelApi<S>(game: GameId) {
  const paths = DUEL_PATHS[game]
  return {
    createRoom: (settings: S, mode: RoomMode = 'pvp', opts: { humanFirst?: boolean } = {}) =>
      request<CreateRoomResponse>(paths.rooms, {
        method: 'POST',
        body: JSON.stringify(
          mode === 'cpu'
            ? { ...settings, mode, humanFirst: opts.humanFirst ?? false }
            : { ...settings, mode },
        ),
      }),
    cpuStats: () => request<CpuStats>(paths.stats),
    /** Persistence is fire-and-forget after a game ends; give it a beat before asking. */
    cpuStatsSoon: () =>
      new Promise((resolve) => setTimeout(resolve, 400)).then(() => request<CpuStats>(paths.stats)),
    roomSummary: (code: string) =>
      request<RoomSummary>(`${paths.rooms}/${encodeURIComponent(code)}`),
  }
}

export const api = {
  me: () => request<Me>('/api/me'),
  updateProfile: (patch: ProfilePatch) =>
    request<Me>('/api/me', { method: 'PATCH', body: JSON.stringify(patch) }),
  createIceRoom: () => request<CreateRoomResponse>('/api/ice/rooms', { method: 'POST' }),
  createCyclesRoom: (settings: CyclesSettings) =>
    request<CreateRoomResponse>('/api/cycles/rooms', {
      method: 'POST',
      body: JSON.stringify(settings),
    }),
  leaderboard: (game: GameId) => request<LeaderboardEntry[]>(`/api/leaderboard?game=${game}`),
  online: () => request<OnlineResponse>('/api/arena/online'),
  challenge: (to: string, game: ChallengeGame) =>
    request<CreateRoomResponse>('/api/arena/challenge', {
      method: 'POST',
      body: JSON.stringify({ to, game }),
    }),
  today: () => request<TodayResponse>('/api/today'),
  history: () => request<HistoryEntry[]>('/api/me/history'),
  // Daily Dungeon
  dungeonToday: () => request<DungeonToday>('/api/dungeon/today'),
  startDungeonRun: (body: StartRunBody) =>
    request<{ run: DungeonView }>('/api/dungeon/runs', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  dungeonRun: (id: string) => request<{ view: DungeonView }>(`/api/dungeon/runs/${id}`),
  dungeonAct: (id: string, body: ActBody) =>
    request<ActResponse>(`/api/dungeon/runs/${id}/act`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  abandonDungeonRun: (id: string) =>
    request<ActResponse>(`/api/dungeon/runs/${id}/abandon`, { method: 'POST' }),
  dungeonBoard: (day?: string) =>
    request<DailyEntry[]>(day ? `/api/dungeon/daily?day=${day}` : '/api/dungeon/daily'),
  myDungeonRuns: () => request<RunSummary[]>('/api/dungeon/runs'),
  // Device transfer
  transferCode: () => request<TransferCodeResponse>('/api/me/transfer', { method: 'POST' }),
  redeemTransfer: (code: string) =>
    request<Me>('/api/me/transfer/redeem', { method: 'POST', body: JSON.stringify({ code }) }),
}
